import type { GrowthContent, GrowthCategory } from '@mc/api'
import {
  track as mcTrack,
  NavigationEvents,
  trackGoldStandardEvent,
  getPageSource,
} from '@mc/track-event'
import {
  ActionTypes,
  KeyTypes,
  saveUserData,
  getTrackedId,
} from '@mc/user-data'
import { Subcat } from './dataFetching'

type Values<T> = T[keyof T]

const BROWSE_ACTION = 'Browse Category Dropdown'

type VariableActions =
  | `${typeof BROWSE_ACTION}:${string}`
  | `Header Nav Purchase CTA:${string}`

const LEVELS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  QUATERNARY: 'quaternary',
} as const

type NavEventOpts = {
  action: typeof BROWSE_ACTION | VariableActions
  level: Values<typeof LEVELS>
  page_source: string | undefined
  course_id?: GrowthContent['contentId']
  camp_id?: GrowthContent['contentId']
  slug?: GrowthContent['slug']
  category?: GrowthCategory['name']
  subcategory?: Subcat['name']
  order?: number
  stored_category_id?: string
  stored_subcategory_id?: Subcat['id'] | string
}

const trackNavClicked = (opts: NavEventOpts) =>
  trackGoldStandardEvent(NavigationEvents.EVENTS.NAVIGATION_CLICKED, opts)

// Category/Browse Drop Down Click Interaction
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#Category%2FBrowse-Drop-Down-Click-Interaction

export const trackBrowseClicked = () => {
  trackNavClicked({
    action: BROWSE_ACTION,
    level: LEVELS.PRIMARY,
    page_source: getPageSource(),
  })
}

// Main Category Click Interaction
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#Main-Category-Click-Interaction

export const trackMainCategoryClicked = ({
  categoryName,
  order,
  id,
}: {
  categoryName: string
  order: number
  id: string
}) => {
  const key = KeyTypes.CATEGORY
  const catId = getTrackedId({ key, id })

  trackNavClicked({
    action: `${BROWSE_ACTION}:${categoryName}`,
    level: LEVELS.SECONDARY,
    page_source: getPageSource(),
    order,
    stored_category_id: catId,
  })

  saveUserData({
    key,
    id: catId,
    actionType: ActionTypes.BROWSING,
    eventName: NavigationEvents.EVENTS.NAVIGATION_CLICKED,
  })
}

// Sub Category Click Interaction
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#Sub-Category-Click-Interaction

export const trackSubCategoryClicked = ({
  catName,
  subcatName,
  subcatId,
}: {
  catName: string
  subcatName: string
  subcatId: string | number
}) => {
  const key = KeyTypes.SUBCATEGORY
  const subcategoryId = getTrackedId({ key, id: subcatId })

  trackNavClicked({
    action: `${BROWSE_ACTION}:${catName}:${subcatName}`,
    category: catName,
    level: LEVELS.TERTIARY,
    page_source: getPageSource(),
    stored_subcategory_id: subcategoryId,
  })

  saveUserData({
    key,
    id: subcategoryId,
    actionType: ActionTypes.BROWSING,
    eventName: NavigationEvents.EVENTS.NAVIGATION_CLICKED,
  })
}

// Course/Instructor Click Interaction
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#Course%2FInstructor-Click-Interaction

export const trackInstructorClick = ({
  id,
  slug,
  type,
  catName,
  subcatName,
}: {
  id: GrowthContent['contentId']
  slug: GrowthContent['slug']
  type: GrowthContent['type']
  catName: string
  subcatName: string | null
}) => {
  const catIdentifier = subcatName ? `${catName}:${subcatName}` : catName
  const level = subcatName ? LEVELS.QUATERNARY : LEVELS.TERTIARY

  trackNavClicked({
    action: `${BROWSE_ACTION}:${catIdentifier}:class selected`,
    category: catName,
    subcategory: subcatName ?? undefined,
    level,
    page_source: getPageSource(),
    course_id: type === 'course' || type === 'series' ? id : undefined,
    camp_id: type === 'camp' ? id : undefined,
    slug,
  })
}

// View All Course Click Interaction
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#View-All-Course-Click-Interaction

export const trackViewAllClick = ({
  catName,
  subcatName,
  actionCta,
}: {
  catName: string
  subcatName: string | null
  actionCta: `View All Classes_${string}`
}) => {
  const catIdentifier = subcatName ? `${catName}:${subcatName}` : catName
  const level = subcatName ? LEVELS.QUATERNARY : LEVELS.TERTIARY

  // i.e: Browse Category Dropdown:Arts & Entertainment:Acting & Comedy:View All Classes_68
  trackNavClicked({
    action: `${BROWSE_ACTION}:${catIdentifier}:${actionCta}`,
    level,
    category: catName,
    subcategory: subcatName ?? undefined,
    page_source: getPageSource(),
  })
}

// Search Related Interactions Due To UI Changes
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/WIP+Nav+Search+Redesign#Search-Related-Interactions-Due-To-UI-Changesa

// Popular Search Related Interactions

export const trackPopularSearchClick = ({
  popularSearchPhrase,
}: {
  popularSearchPhrase: string
}) => {
  mcTrack('Search String Entered', {
    string: `Popular Search:${popularSearchPhrase}`,
    page_source: getPageSource(),
  })
}

// Other Nav Tracking Potentially Impacted Due To UI Changes
// https://masterclass-dev.atlassian.net/wiki/spaces/DATA/pages/2222948353/Nav+Search+Redesign#Other-Nav-Tracking-Potentially-Impacted-Due-To-UI-Changes

export const trackSignUpCTAClick = (action: string) => {
  trackNavClicked({
    action: `Header Nav Purchase CTA:${action}`,
    level: LEVELS.PRIMARY,
    page_source: getPageSource(),
  })
}

// Search Box Click in Nav

export const trackSearchStart = () =>
  mcTrack('Search Start', {
    page_source: getPageSource(),
  })
